import React, { useState, useEffect } from 'react';
import { useProject } from '../context/ProjectContext.tsx';
import { literatureService } from '../services/literatureService.ts';
import { Literature } from '../types';
import '../styles/FindDuplicates.css';
import { reviewService } from '../services/reviewService.ts';

// Define interfaces for duplicate finding
interface DuplicateItem extends Literature {
    similarity?: number;
}

interface DuplicateGroup {
    original: DuplicateItem;
    duplicates: DuplicateItem[];
}

// Response type from the API
type FindDuplicatesResponse = {
    duplicate_groups: DuplicateGroup[];
    total_groups: number;
    papers_checked: number;
    limit_applied: number;
    offset: number;
    chunk_size: number;
    current_chunk: number;
    total_count: number;
    has_more: boolean;
    next_offset: number | null;
};

export const FindDuplicates: React.FC = () => {
    const { currentProject } = useProject();
    const [findingDuplicates, setFindingDuplicates] = useState(false);
    const [duplicateGroups, setDuplicateGroups] = useState<DuplicateGroup[]>([]);
    const [papersChecked, setPapersChecked] = useState<number>(0);
    const [chunkSize] = useState<number>(1000);
    const [currentOffset, setCurrentOffset] = useState<number>(0);
    const [hasMoreChunks, setHasMoreChunks] = useState<boolean>(false);
    const [totalPapers, setTotalPapers] = useState<number>(0);
    const [message, setMessage] = useState('');

    useEffect(() => {
        if (currentProject) {
            // Clear any previous results when project changes
            setDuplicateGroups([]);
            setPapersChecked(0);
            setCurrentOffset(0);
            setHasMoreChunks(false);
            setTotalPapers(0);
            setMessage('');
        }
    }, [currentProject]);

    // Function to find duplicate entries
    const findDuplicates = async (resetResults: boolean = true) => {
        if (!currentProject) return;

        setFindingDuplicates(true);

        // Reset state if starting a new search
        if (resetResults) {
            setCurrentOffset(0);
            setDuplicateGroups([]);
            setPapersChecked(0);
            setMessage('Searching for duplicate entries using parallel processing...');
        } else {
            setMessage(`Loading more duplicates using parallel processing...`);
        }

        try {
            // Use a larger chunk size for better performance
            const result = await literatureService.findDuplicates(
                currentProject.id,
                12000, // Process up to 12000 papers (entire dataset)
                currentOffset, // Start from the current offset
                chunkSize // Use the configured chunk size
            );

            // Update state with the new results
            if (resetResults) {
                setDuplicateGroups(result.duplicate_groups);
                setPapersChecked(result.papers_checked);
            } else {
                setDuplicateGroups((prevGroups) => [...prevGroups, ...result.duplicate_groups]);
                setPapersChecked((prevChecked) => prevChecked + result.papers_checked);
            }

            setTotalPapers(result.total_count);
            setHasMoreChunks(result.has_more);

            // Update offset for next chunk
            if (result.has_more && result.next_offset !== null) {
                setCurrentOffset(result.next_offset);
            }

            if (result.total_groups > 0) {
                setMessage(
                    `Found ${
                        resetResults ? result.total_groups : result.duplicate_groups.length
                    } groups of potential duplicates. ` +
                        `Checked papers ${result.offset + 1}-${result.offset + result.papers_checked} of ${
                            result.total_count
                        }.` +
                        (result.has_more ? ' Click "Load More" to continue.' : '')
                );
            } else {
                setMessage(
                    `No duplicate entries found in this chunk. ` +
                        `Checked papers ${result.offset + 1}-${result.offset + result.papers_checked} of ${
                            result.total_count
                        }.` +
                        (result.has_more ? ' Click "Load More" to continue.' : '')
                );
            }
        } catch (error) {
            console.error('Error finding duplicates:', error);
            setMessage('Failed to find duplicate entries');
        } finally {
            setFindingDuplicates(false);
        }
    };

    // Function to load the next chunk of duplicates
    const loadMoreDuplicates = () => {
        findDuplicates(false);
    };

    // Function to merge duplicate entries
    const mergeDuplicates = async (originalId: number, duplicateId: number) => {
        try {
            // Show confirmation dialog before merging
            const confirmMerge = window.confirm(
                'Are you sure you want to merge these entries? The duplicate entry will be deleted after merging.'
            );

            if (!confirmMerge) {
                return; // User cancelled the merge
            }

            // Get the original and duplicate items
            const group = duplicateGroups.find((g) => g.original.id === originalId);
            if (!group) return;

            const duplicate = group.duplicates.find((d) => d.id === duplicateId);
            if (!duplicate) return;

            setMessage('Merging entries...');

            let updatesMade = false;

            // If the original doesn't have a DOI but the duplicate does, copy the DOI
            if ((!group.original.doi || group.original.doi === '') && duplicate.doi) {
                await literatureService.updateLiterature(originalId, {
                    doi: duplicate.doi,
                });
                updatesMade = true;
            }

            // If the original doesn't have full text but the duplicate does, mark it as available
            if (!group.original.full_text_available && duplicate.full_text_available) {
                await literatureService.updateLiterature(originalId, {
                    full_text_available: true,
                });
                updatesMade = true;
            }

            // If the original has an empty or missing field that the duplicate has, copy it
            const updateData: any = {};

            if ((!group.original.authors || group.original.authors === '') && duplicate.authors) {
                updateData.authors = duplicate.authors;
                updatesMade = true;
            }

            if ((!group.original.journal || group.original.journal === '') && duplicate.journal) {
                updateData.journal = duplicate.journal;
                updatesMade = true;
            }

            if (!group.original.year && duplicate.year) {
                updateData.year = duplicate.year;
                updatesMade = true;
            }

            // Apply additional updates if needed
            if (Object.keys(updateData).length > 0) {
                await literatureService.updateLiterature(originalId, updateData);
            }

            // Now delete the duplicate entry from the database
            await literatureService.deleteLiterature(duplicateId);

            // Remove the duplicate from the UI
            setDuplicateGroups((prevGroups) =>
                prevGroups
                    .map((g) => {
                        if (g.original.id === originalId) {
                            return {
                                ...g,
                                duplicates: g.duplicates.filter((d) => d.id !== duplicateId),
                            };
                        }
                        return g;
                    })
                    .filter((g) => g.duplicates.length > 0)
            );

            if (updatesMade) {
                setMessage(`Entries merged successfully. Duplicate entry has been deleted.`);
            } else {
                setMessage(`No updates needed. Duplicate entry has been deleted.`);
            }
        } catch (error) {
            console.error('Error merging duplicates:', error);
            setMessage('Failed to merge entries');
        }
    };

    // Function to delete a duplicate entry
    const deleteDuplicate = async (duplicateId: number) => {
        // Show confirmation dialog before deleting
        const confirmDelete = window.confirm(
            'Are you sure you want to delete this entry? This action cannot be undone.'
        );

        if (!confirmDelete) {
            return; // User cancelled the deletion
        }

        try {
            setMessage('Deleting duplicate entry...');

            // Call the API to delete the entry from the database
            const result = await literatureService.deleteLiterature(duplicateId);

            // Update the UI to remove the deleted entry
            setDuplicateGroups((prevGroups) =>
                prevGroups
                    .map((g) => ({
                        ...g,
                        duplicates: g.duplicates.filter((d) => d.id !== duplicateId),
                    }))
                    .filter((g) => g.duplicates.length > 0)
            );

            setMessage(result.message || 'Duplicate entry deleted successfully.');
        } catch (error) {
            console.error('Error deleting duplicate:', error);
            setMessage('Failed to delete duplicate entry');
        }
    };

    // Function to delete the original paper and keep a duplicate
    const deleteOriginalKeepDuplicate = async (groupId: number, duplicateId: number) => {
        // Show confirmation dialog before deleting
        const confirmDelete = window.confirm(
            'Are you sure you want to delete the original entry and keep this duplicate? This action cannot be undone.'
        );

        if (!confirmDelete) {
            return; // User cancelled the deletion
        }

        try {
            setMessage('Deleting original entry...');

            // Find the group and duplicate
            const group = duplicateGroups.find((g) => g.original.id === groupId);
            if (!group) {
                setMessage('Error: Group not found');
                return;
            }

            const duplicate = group.duplicates.find((d) => d.id === duplicateId);
            if (!duplicate) {
                setMessage('Error: Duplicate not found');
                return;
            }

            // Call the API to delete the original entry
            await literatureService.deleteLiterature(groupId);

            // Update the UI to remove the group
            setDuplicateGroups((prevGroups) => prevGroups.filter((g) => g.original.id !== groupId));

            setMessage(`Original entry deleted successfully. Kept duplicate entry with ID ${duplicateId}.`);
        } catch (error) {
            console.error('Error deleting original entry:', error);
            setMessage('Failed to delete original entry');
        }
    };

    return (
        <div className="find-duplicates-container">
            <div className="find-duplicates-explanation">
                <h2>Find Duplicate Entries</h2>
                <p>
                    This tool helps you identify and manage potential duplicate entries in your literature
                    database. Duplicates are identified based on similar titles and authors.
                </p>
                <p>
                    <strong>Note:</strong> To ensure good performance, the search processes papers in chunks of{' '}
                    {chunkSize} at a time using parallel processing. You can load more chunks by clicking the
                    "Load More" button when available.
                </p>
            </div>

            {message && <div className="find-duplicates-message">{message}</div>}

            {papersChecked > 0 && (
                <div className="find-duplicates-stats">
                    <h3>Statistics</h3>
                    <ul>
                        <li>
                            <strong>Papers checked:</strong> {papersChecked} of {totalPapers}
                        </li>
                        <li>
                            <strong>Duplicate groups found:</strong> {duplicateGroups.length}
                        </li>
                        <li>
                            <strong>Chunk size:</strong> {chunkSize}
                        </li>
                        <li>
                            <strong>Current offset:</strong> {currentOffset}
                        </li>
                        <li>
                            <strong>More chunks available:</strong> {hasMoreChunks ? 'Yes' : 'No'}
                        </li>
                    </ul>
                </div>
            )}

            <div className="find-duplicates-actions">
                <button
                    onClick={() => findDuplicates(true)}
                    disabled={findingDuplicates || !currentProject}
                    className="primary-button">
                    {findingDuplicates && currentOffset === 0
                        ? 'Finding Duplicates...'
                        : 'Find Duplicate Entries'}
                </button>

                {hasMoreChunks && (
                    <button
                        onClick={loadMoreDuplicates}
                        disabled={findingDuplicates || !currentProject}
                        className="secondary-button">
                        {findingDuplicates && currentOffset > 0 ? 'Loading More...' : 'Load More'}
                    </button>
                )}
            </div>

            {duplicateGroups.length > 0 ? (
                <div className="duplicate-groups">
                    <h3>Potential Duplicate Entries</h3>
                    <p className="duplicate-explanation">
                        These are potential duplicate entries found in your project. You can:
                        <ul>
                            <li>
                                <strong>Merge & Delete</strong>: Merge information from duplicates to the
                                original entry and delete the duplicate.
                            </li>
                            <li>
                                <strong>Keep This (Delete Original)</strong>: Keep the duplicate entry and
                                delete the original.
                            </li>
                            <li>
                                <strong>Delete Entry</strong>: Delete the duplicate entry without affecting the
                                original.
                            </li>
                        </ul>
                    </p>

                    {duplicateGroups.map((group) => (
                        <div key={group.original.id} className="duplicate-group">
                            <div className="original-item">
                                <h4>Original Entry</h4>
                                <div className="literature-details">
                                    <h5>{group.original.title}</h5>
                                    <p>
                                        <strong>Authors:</strong> {group.original.authors}
                                    </p>
                                    <p>
                                        <strong>Journal:</strong> {group.original.journal}
                                    </p>
                                    <p>
                                        <strong>Year:</strong> {group.original.year}
                                    </p>
                                    <p>
                                        <strong>DOI:</strong>{' '}
                                        {group.original.doi ? (
                                            <a
                                                href={reviewService.getDoiUrl(group.original.doi)}
                                                target="_blank"
                                                rel="noopener noreferrer">
                                                {group.original.doi}
                                            </a>
                                        ) : (
                                            'None'
                                        )}
                                    </p>
                                    <p>
                                        <strong>Full Text Available:</strong>{' '}
                                        {group.original.full_text_available ? 'Yes' : 'No'}
                                    </p>
                                </div>
                            </div>

                            <div className="duplicate-items">
                                <h4>Potential Duplicates</h4>
                                {group.duplicates.map((duplicate) => (
                                    <div key={duplicate.id} className="duplicate-item">
                                        <div className="literature-details">
                                            <h5>{duplicate.title}</h5>
                                            <p>
                                                <strong>Authors:</strong> {duplicate.authors}
                                            </p>
                                            <p>
                                                <strong>Journal:</strong> {duplicate.journal}
                                            </p>
                                            <p>
                                                <strong>Year:</strong> {duplicate.year}
                                            </p>
                                            <p>
                                                <strong>DOI:</strong>{' '}
                                                {duplicate.doi ? (
                                                    <a
                                                        href={reviewService.getDoiUrl(duplicate.doi)}
                                                        target="_blank"
                                                        rel="noopener noreferrer">
                                                        {duplicate.doi}
                                                    </a>
                                                ) : (
                                                    'None'
                                                )}
                                            </p>
                                            <p>
                                                <strong>Full Text Available:</strong>{' '}
                                                {duplicate.full_text_available ? 'Yes' : 'No'}
                                            </p>
                                            <p>
                                                <strong>Similarity Score:</strong>{' '}
                                                {duplicate.similarity
                                                    ? Math.round(duplicate.similarity * 100)
                                                    : 0}
                                                %
                                            </p>
                                        </div>
                                        <div className="duplicate-actions">
                                            <button
                                                onClick={() => mergeDuplicates(group.original.id, duplicate.id)}
                                                className="merge-button">
                                                Merge & Delete
                                            </button>
                                            <button
                                                onClick={() =>
                                                    deleteOriginalKeepDuplicate(group.original.id, duplicate.id)
                                                }
                                                className="keep-button">
                                                Keep This (Delete Original)
                                            </button>
                                            <button
                                                onClick={() => deleteDuplicate(duplicate.id)}
                                                className="delete-button">
                                                Delete Entry
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="no-duplicates">
                    <p>No duplicate entries found. Click "Find Duplicate Entries" to search for duplicates.</p>
                </div>
            )}
        </div>
    );
};
